// The capped widgets on the server-rendered overview pages (hike, recording, peak
// list): the server draws the preview (app/services/overview_section_preview.rb), and
// this wires up the controls that open and close it in place. Every rule about how
// much shows comes from overviewSectionPreview.ts; this only touches the DOM.
//
// Markup, all of it rendered by the server so the page reads right before any script:
//
//   Items — <div data-overview-section="nearby_hikes" data-overview-total="7">
//             <… data-overview-item> × total (past the preview: `hidden`)
//             <button data-overview-toggle data-label-more="…" data-label-less="…">
//
//   Text  — <div data-overview-clamp>
//             <p class="overview-clamp" data-overview-clamp-text>…</p>
//             <button data-overview-clamp-toggle data-label-more="…" data-label-less="…" hidden>
//
//   Tags  — <div data-overview-tags>
//             <… data-overview-tag> × n
//             <button data-overview-tags-more hidden>
import {
  OVERVIEW_LINES,
  OVERVIEW_SECTIONS,
  overviewSectionPreview,
  type OverviewSection,
} from './overviewSectionPreview';

const BOUND = 'overviewBound';

function claim(el: HTMLElement): boolean {
  if (el.dataset[BOUND]) return false;
  el.dataset[BOUND] = '1';
  return true;
}

// ── Items: Show more / Show less in place ───────────────────────────────────

/** Draws a section for one expanded state; returns the action under the list. */
export function applySectionPreview(section: HTMLElement, expanded: boolean): string {
  const name = section.dataset.overviewSection as OverviewSection;
  if (!(name in OVERVIEW_SECTIONS)) return 'none';
  const items = Array.from(section.querySelectorAll<HTMLElement>('[data-overview-item]'));
  const { shown, action } = overviewSectionPreview(name, items.length, expanded);
  items.forEach((item, index) => { item.hidden = index >= shown; });

  const toggle = section.querySelector<HTMLElement>('[data-overview-toggle]');
  if (toggle) {
    toggle.hidden = action !== 'show_more' && action !== 'show_less';
    toggle.textContent = (action === 'show_less' ? toggle.dataset.labelLess : toggle.dataset.labelMore) || '';
    toggle.setAttribute('aria-expanded', String(action === 'show_less'));
  }
  return action;
}

export function bindOverviewSection(section: HTMLElement): void {
  if (!claim(section)) return;
  let expanded = false;
  applySectionPreview(section, expanded);
  section.querySelector<HTMLElement>('[data-overview-toggle]')?.addEventListener('click', (event) => {
    event.preventDefault();
    expanded = !expanded;
    applySectionPreview(section, expanded);
  });
}

// ── Long text: 3 lines, with a toggle only when it was actually cut ─────────

/** True when the clamped element hides some of its text. */
export function isClamped(el: HTMLElement): boolean {
  return el.scrollHeight > el.clientHeight + 1;
}

export function bindOverviewClamp(root: HTMLElement): void {
  const text = root.querySelector<HTMLElement>('[data-overview-clamp-text]');
  const toggle = root.querySelector<HTMLElement>('[data-overview-clamp-toggle]');
  if (!text || !toggle || !claim(root)) return;

  text.style.setProperty('--overview-clamp-lines', String(OVERVIEW_LINES.description_lines));
  let expanded = false;
  const draw = (): void => {
    text.classList.toggle('overview-clamp', !expanded);
    toggle.textContent = (expanded ? toggle.dataset.labelLess : toggle.dataset.labelMore) || '';
    toggle.setAttribute('aria-expanded', String(expanded));
    // Collapsed, the toggle is only there when the clamp hid something.
    toggle.hidden = !expanded && !isClamped(text);
  };
  draw();
  toggle.addEventListener('click', (event) => {
    event.preventDefault();
    expanded = !expanded;
    draw();
  });
  onLayoutChange(() => { if (!expanded) draw(); });
}

// ── Tags: at most two rows, the rest behind a "+N" chip ─────────────────────

/** Rows a set of chip tops spans; chips on one row share a top give or take a pixel. */
export function countRows(tops: number[], tolerance = 2): number {
  const rows: number[] = [];
  tops.forEach((top) => {
    if (!rows.some((row) => Math.abs(row - top) <= tolerance)) rows.push(top);
  });
  return rows.length;
}

/**
 * Hides the chips that don't fit in `maxRows`, with a "+N" chip for them on the
 * last row. Returns how many chips are still showing.
 */
export function foldTags(chips: HTMLElement[], more: HTMLElement, maxRows: number): number {
  chips.forEach((chip) => { chip.hidden = false; });
  more.hidden = true;
  if (countRows(chips.map((chip) => chip.offsetTop)) <= maxRows) return chips.length;

  more.hidden = false;
  let shown = chips.length;
  while (shown > 0) {
    shown -= 1;
    chips[shown].hidden = true;
    more.textContent = `+${chips.length - shown}`;
    const tops = chips.slice(0, shown).map((chip) => chip.offsetTop).concat(more.offsetTop);
    if (countRows(tops) <= maxRows) break;
  }
  return shown;
}

export function bindOverviewTags(root: HTMLElement): void {
  const more = root.querySelector<HTMLElement>('[data-overview-tags-more]');
  if (!more || !claim(root)) return;
  const chips = Array.from(root.querySelectorAll<HTMLElement>('[data-overview-tag]'));
  let expanded = false;
  const fold = (): void => { if (!expanded) foldTags(chips, more, OVERVIEW_LINES.tag_rows); };
  fold();
  // Every tag, in place, for good: there is no chip to fold them back.
  more.addEventListener('click', (event) => {
    event.preventDefault();
    expanded = true;
    chips.forEach((chip) => { chip.hidden = false; });
    more.hidden = true;
  });
  onLayoutChange(fold);
}

// ── Wiring ──────────────────────────────────────────────────────────────────

function onLayoutChange(callback: () => void): void {
  let frame = 0;
  const schedule = (): void => {
    if (frame) cancelAnimationFrame(frame);
    frame = requestAnimationFrame(() => { frame = 0; callback(); });
  };
  window.addEventListener('resize', schedule);
  // Web fonts change chip widths and line breaks once they land.
  (document as Document & { fonts?: { ready?: Promise<unknown> } }).fonts?.ready?.then(schedule).catch(() => {});
}

/** Wires every capped widget under `root`; safe to call again (each binds once). */
export function initOverviewPreviews(root: ParentNode = document): void {
  root.querySelectorAll<HTMLElement>('[data-overview-section]').forEach(bindOverviewSection);
  root.querySelectorAll<HTMLElement>('[data-overview-clamp]').forEach(bindOverviewClamp);
  root.querySelectorAll<HTMLElement>('[data-overview-tags]').forEach(bindOverviewTags);
}
